import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { AdminSubscriptionItem } from "@/types/subscription";
import { useGrantCredits } from "@/hooks/use-subscriptions";

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 100000;

const grantSchema = z.object({
  amount: z
    .string()
    .trim()
    .min(1, "값을 입력하세요")
    .refine((value) => /^\d+$/.test(value), "정수만 입력 가능합니다")
    .refine((value) => {
      const parsed = Number(value);
      return parsed >= MIN_AMOUNT && parsed <= MAX_AMOUNT;
    }, `${MIN_AMOUNT.toLocaleString()} ~ ${MAX_AMOUNT.toLocaleString()} 사이의 값이어야 합니다`),
  reason: z.string().max(500).optional(),
});

type GrantFormValues = z.infer<typeof grantSchema>;

interface GrantCreditsDialogProps {
  subscription: AdminSubscriptionItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GrantCreditsDialog({
  subscription,
  open,
  onOpenChange,
}: GrantCreditsDialogProps) {
  const { mutate: grant, isPending } = useGrantCredits();

  const form = useForm<GrantFormValues>({
    resolver: zodResolver(grantSchema),
    defaultValues: {
      amount: "",
      reason: "",
    },
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) form.reset();
    onOpenChange(isOpen);
  };

  const onSubmit = (values: GrantFormValues) => {
    grant(
      {
        userId: subscription.userId,
        amount: Number(values.amount),
        reason: values.reason || undefined,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>구매 크레딧 지급</DialogTitle>
          <DialogDescription>
            사용자의 구매 크레딧(purchased)에 양의 정수를 더합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">사용자</span>
              <span className="text-sm">{subscription.userName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">이메일</span>
              <span className="text-sm">{subscription.userEmail}</span>
            </div>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>지급 크레딧 *</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder={`${MIN_AMOUNT.toLocaleString()} ~ ${MAX_AMOUNT.toLocaleString()}`}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>사유 (선택)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="지급 사유를 입력하세요..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isPending}
            aria-label="Confirm grant credits"
          >
            {isPending ? "처리 중..." : "지급"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
